import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiPlay, FiPlus, FiX, FiArrowDown } from "react-icons/fi";
import { aboutData } from "./HomeData";
import { updateViewCount } from "../Backend";
import { db } from "../firebase-config";
import { doc } from "firebase/firestore";
import { useLanguage } from "../Language";
import Examples from "./Examples";
import Products from "./Products";
import Contact from "./Contact";
import "./Home.css";

const scrollStoryMedia =
  "(min-width: 761px) and (min-height: 600px) and (prefers-reduced-motion: no-preference)";

const spaces = [
  {
    id: "sports",
    title: "Sport- & Spielanlagen",
    text: "Vom ersten Versuch bis zur nächsten Challenge. Inhalte, die eure Anlage erlebbar machen.",
    heading: "Mehr aus eurer Anlage machen.",
    idea: "Tutorials erklären die Geräte. Workouts und Parkour-Challenges laden dazu ein, neue Bewegungen auszuprobieren.",
    image: "/images/IMG_7064.jpg",
    alt: "Ein Mann scannt mit seinem Smartphone einen QR-Code an einer Sportanlage.",
  },
  {
    id: "parks",
    title: "Parks & öffentliche Räume",
    text: "Neue Bewegungsimpulse für die Orte, an denen Menschen ohnehin zusammenkommen.",
    heading: "Den Alltag in Bewegung bringen.",
    idea: "Ein Workout an einer Bank, eine Balance-Übung oder ein Bewegungsspiel: Kleine Impulse eröffnen neue Möglichkeiten für vorhandene Orte.",
    image: "/images/IMG_7056.jpg",
    alt: "Ein QUICR-Code an einer Boulderwand lädt zum Mitmachen ein.",
  },
  {
    id: "temporary",
    title: "Temporäre Bewegungsräume",
    text: "Ob Aktionstag oder ungewöhnlicher Standort: Bewegung braucht nicht immer einen Sportplatz.",
    heading: "Raum für eure nächste Aktion.",
    idea: "Für einen Aktionstag oder einen Standort auf Zeit entwickeln wir passende Bewegungsideen. Ein QR-Code macht sie direkt vor Ort zugänglich.",
    image: "/images/IMG_6915.jpg",
    alt: "Ein Teammitglied steuert eine Drohne für eine Aufnahme an einer Bewegungsanlage.",
  },
];

const services = [
  {
    title: "Individuelle Challenges, Workouts & Tutorials",
    text: "Wir entwickeln Übungen, Spiele und Challenges, die zu eurem Ort und seinen Nutzerinnen und Nutzern passen.",
  },
  {
    title: "Videoproduktion direkt vor Ort",
    text: "Wir filmen an eurer Anlage. So zeigen die Videos die Geräte und Bewegungsmöglichkeiten, die Besucherinnen und Besucher direkt vor sich haben.",
  },
  {
    title: "Herstellung der QR-Code-Tiles",
    text: "Wir produzieren die physischen QR-Code-Tiles. Sie verbinden die jeweilige Station mit den passenden digitalen Inhalten.",
  },
  {
    title: "Verwaltung & Hosting der Inhalte",
    text: "Wir kümmern uns um die Zuordnung der QR-Codes und das Hosting der Inhalte. Die Videos öffnen sich direkt im Browser, ohne App-Download.",
  },
  {
    title: "Auf Wunsch in eurem Branding",
    text: "Auf Wunsch stimmen wir die Gestaltung der Inhalte auf euren Auftritt ab. Eure Anlage und eure Marke bleiben im Mittelpunkt.",
  },
];

function Home() {
  const { t } = useLanguage();
  const { search, key: navigationKey } = useLocation();
  const trailer = useRef(null);
  const video = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(spaces[0].id);
  const [openService, setOpenService] = useState(0);
  const serviceList = useRef(null);
  const serviceTriggers = useRef([]);
  const serviceContents = useRef([]);
  const storySteps = useRef([]);
  const [scrollStory, setScrollStory] = useState(
    () => window.matchMedia?.(scrollStoryMedia).matches ?? false,
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const preference = window.matchMedia(scrollStoryMedia);
    const updatePreference = () => setScrollStory(preference.matches);
    updatePreference();
    preference.addEventListener("change", updatePreference);
    return () => preference.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    if (!scrollStory) return;
    let frame;
    let settleTimer;
    let gestureStart;
    let lastScrollY = window.scrollY;
    let touching = false;
    const positions = () =>
      storySteps.current.map((step) => {
        const bounds = step.getBoundingClientRect();
        return (
          window.scrollY +
          bounds.top +
          bounds.height / 2 -
          window.innerHeight / 2
        );
      });
    const cancelSettlement = () => {
      window.clearTimeout(settleTimer);
      gestureStart = undefined;
      touching = false;
    };
    const settleStory = () => {
      const start = gestureStart;
      cancelSettlement();
      if (start === undefined) return;
      const stops = positions();
      const current = window.scrollY;
      const distance = current - start;
      if (Math.abs(distance) < 8) return;
      const examples = document.getElementById("examples");
      if (examples && distance >= 40) {
        const examplesStart =
          current +
          examples.getBoundingClientRect().top -
          (parseFloat(window.getComputedStyle(examples).scrollMarginTop) || 0);
        if (
          start >= stops[stops.length - 1] - 12 &&
          start < examplesStart - 12 &&
          current <= examplesStart + window.innerHeight / 4
        ) {
          examples.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }
      // Snap when entering, but allow free scrolling outside the story-to-examples transition.
      if (
        current < stops[0] &&
        (distance < 0 || current < stops[0] - window.innerHeight / 4)
      )
        return;
      if (
        current > stops[stops.length - 1] &&
        (distance > 0 ||
          current > stops[stops.length - 1] + window.innerHeight / 4)
      )
        return;
      let target = stops.reduce((closest, stop) =>
        Math.abs(stop - current) < Math.abs(closest - current) ? stop : closest,
      );
      const origin = stops.findIndex((stop) => Math.abs(stop - start) < 12);
      if (origin !== -1 && Math.abs(distance) >= 40) {
        target =
          stops[
            Math.max(
              0,
              Math.min(stops.length - 1, origin + Math.sign(distance)),
            )
          ];
      }
      window.scrollTo({ top: target, behavior: "smooth" });
    };
    const beginGesture = () => {
      window.clearTimeout(settleTimer);
      // Passive wheel events may arrive after the compositor has already scrolled.
      if (gestureStart === undefined) gestureStart = lastScrollY;
    };
    const beginWheel = (event) => {
      if (
        !event.defaultPrevented &&
        !event.ctrlKey &&
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
      )
        beginGesture();
    };
    const beginTouch = () => {
      touching = true;
      beginGesture();
    };
    const endTouch = () => {
      touching = false;
      if (gestureStart !== undefined)
        settleTimer = window.setTimeout(settleStory, 140);
    };
    const onKeyDown = (event) => {
      if (
        event.defaultPrevented ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.target.closest?.(
          "input, textarea, select, button, a, [contenteditable]",
        )
      )
        return;
      if (
        ["ArrowDown", "ArrowUp", "PageDown", "PageUp", " "].includes(event.key)
      )
        beginGesture();
      else cancelSettlement();
    };
    const updateStory = () => {
      const center = window.innerHeight / 2;
      let closest = 0;
      let distance = Infinity;
      storySteps.current.forEach((step, index) => {
        const bounds = step.getBoundingClientRect();
        const nextDistance = Math.abs(bounds.top + bounds.height / 2 - center);
        if (nextDistance < distance) {
          closest = index;
          distance = nextDistance;
        }
      });
      setSelectedSpace(spaces[closest].id);
      frame = undefined;
    };
    const scheduleUpdate = () => {
      lastScrollY = window.scrollY;
      if (gestureStart !== undefined && !touching) {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(settleStory, 140);
      }
      if (frame === undefined)
        frame = window.requestAnimationFrame(updateStory);
    };
    updateStory();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("wheel", beginWheel, { passive: true });
    window.addEventListener("touchstart", beginTouch, { passive: true });
    window.addEventListener("touchend", endTouch, { passive: true });
    window.addEventListener("touchcancel", cancelSettlement, { passive: true });
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("click", cancelSettlement);
    return () => {
      cancelSettlement();
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("wheel", beginWheel);
      window.removeEventListener("touchstart", beginTouch);
      window.removeEventListener("touchend", endTouch);
      window.removeEventListener("touchcancel", cancelSettlement);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("click", cancelSettlement);
    };
  }, [scrollStory, navigationKey]);

  useLayoutEffect(() => {
    const measure = () => {
      const headings = serviceTriggers.current.reduce(
        (height, trigger) => height + trigger.getBoundingClientRect().height,
        0,
      );
      const content = Math.max(
        ...serviceContents.current.map((panel) => panel.scrollHeight),
      );
      serviceList.current.style.minHeight = `${headings + content + services.length - 1}px`;
    };
    measure();
    const observer = window.ResizeObserver ? new ResizeObserver(measure) : null;
    [...serviceTriggers.current, ...serviceContents.current].forEach(
      (element) => observer?.observe(element),
    );
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [t]);

  const openTrailer = () => {
    setVideoFailed(false);
    trailer.current.showModal();
    updateViewCount(doc(db, "Customers", "v25z", "Codes", "xqkJ")).catch(
      (error) => {
        console.error("Trailer-Aufruf konnte nicht gezählt werden.", error);
      },
    );
  };

  return (
    <>
      <section className="movement-hero" aria-labelledby="home-title">
        <div className="hero-photo">
          <picture>
            <source media="(max-width: 760px)" srcSet="/images/game-lvl1.png" />
            <img
              src="/images/parkour-hero.png"
              alt={t(
                "Ein Sportler springt zwischen zwei Elementen einer Parkouranlage.",
              )}
              width="1967"
              height="799"
            />
          </picture>
          <div className="hero-headline">
            <h1 id="home-title">
              <span>{t("Aus Orten")}</span> <span>{t("wird Bewegung.")}</span>
            </h1>
          </div>
        </div>
        <div className="hero-introduction page-width">
          <p>
            {t(
              "QUICR verbindet Sportanlagen und öffentliche Räume mit Workouts, Spielen und Tutorials. Direkt vor Ort, über einen QR-Code.",
            )}
          </p>
          <div className="hero-actions">
            <Link
              className="action action-primary"
              to={{ search, hash: "#contact" }}
            >
              {t("Projekt besprechen")}
            </Link>
            <button className="trailer-button" onClick={openTrailer}>
              <FiPlay aria-hidden="true" />
              {t("Film ansehen")}
            </button>
          </div>
        </div>
      </section>
      <section className="scan-section page-width" aria-labelledby="scan-title">
        <figure className="tile-photo" data-reveal>
          <img
            src="/images/IMG_7056.jpg"
            alt={t(
              "Ein QUICR-Code an einer Boulderwand lädt zum Mitmachen ein.",
            )}
            width="1152"
            height="768"
            loading="lazy"
          />
        </figure>
        <div className="scan-content">
          <h2 id="scan-title">{t("Ein Code. Ein neuer Impuls.")}</h2>
          <p>
            {t(
              "Ein Scan öffnet passende Übungen und Bewegungsideen. Wir entwickeln die Inhalte und bringen sie an euren Ort.",
            )}
          </p>
          <Link className="text-link" to={{ search, hash: "#examples" }}>
            {t("Beispiele ansehen")}
          </Link>
        </div>
      </section>

      <section
        className={`spaces-section page-width${scrollStory ? " has-scroll-story" : ""}`}
        aria-labelledby="spaces-title"
      >
        <div className="space-story">
          {scrollStory && (
            <div className="space-image">
              {spaces.map((space) => (
                <img
                  key={space.id}
                  className={`scanning-photo${selectedSpace === space.id ? " is-active" : ""}`}
                  src={space.image}
                  alt={t(space.alt)}
                  aria-hidden={selectedSpace !== space.id}
                  width="1152"
                  height="768"
                  loading="lazy"
                />
              ))}
            </div>
          )}
          <div className="space-steps">
            {spaces.map((space, index) => (
              <article
                key={space.id}
                ref={(step) => {
                  storySteps.current[index] = step;
                }}
                className={`space-step${selectedSpace === space.id ? " is-active" : ""}`}
                aria-labelledby={`space-${space.id}`}
              >
                {!scrollStory && (
                  <figure className="space-static-image" data-reveal>
                    <img
                      src={space.image}
                      alt={t(space.alt)}
                      width="1152"
                      height="768"
                      loading="lazy"
                    />
                  </figure>
                )}
                <div className="space-description">
                  {index === 0 && (
                    <h2 id="spaces-title">{t("Der Ort ist schon da.")}</h2>
                  )}
                  <p className="space-category">{t(space.title)}</p>
                  <h3 id={`space-${space.id}`}>{t(space.heading)}</h3>
                  <p>{t(space.text)}</p>
                  <p>{t(space.idea)}</p>
                  <Link
                    className="text-link"
                    to={{ search, hash: "#examples" }}
                  >
                    {t("Beispiele ansehen")}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Examples navigationKey={navigationKey} />
      <section className="service-section" aria-labelledby="service-title">
        <div className="service-layout page-width">
          <div className="service-copy">
            <h2 id="service-title">{t("Wir kümmern uns um den Inhalt.")}</h2>
            <p>
              {t(
                "Wir entwickeln die passenden Inhalte für euren Bewegungsraum und kümmern uns um die Umsetzung.",
              )}
            </p>
            <Link
              className="text-link service-products-link"
              state={{ scrollTransition: true }}
              to={{ search, hash: "#products" }}
            >
              {t("Unsere Produkte")} <FiArrowDown aria-hidden="true" />
            </Link>
          </div>
          <ul className="service-list" ref={serviceList}>
            {services.map((service, index) => (
              <li key={service.title}>
                <button
                  className="service-trigger"
                  ref={(element) => {
                    serviceTriggers.current[index] = element;
                  }}
                  id={`service-trigger-${index}`}
                  aria-expanded={openService === index}
                  aria-controls={`service-panel-${index}`}
                  onClick={() => setOpenService(index)}
                >
                  <span>{t(service.title)}</span>
                  <FiPlus aria-hidden="true" />
                </button>
                <div
                  id={`service-panel-${index}`}
                  className={`service-panel${openService === index ? " is-open" : ""}`}
                  role="region"
                  aria-labelledby={`service-trigger-${index}`}
                  aria-hidden={openService !== index}
                >
                  <div
                    ref={(element) => {
                      serviceContents.current[index] = element;
                    }}
                  >
                    <p>{t(service.text)}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <Products />
      <Contact />
      <dialog
        className="trailer-dialog"
        ref={trailer}
        aria-labelledby="trailer-title"
        onClose={() => video.current?.pause()}
        onClick={(event) => {
          if (event.target === trailer.current) trailer.current.close();
        }}
      >
        <div className="trailer-dialog-heading">
          <h2 id="trailer-title">{t("QUICR in Bewegung")}</h2>
          <button
            aria-label={t("Trailer schließen")}
            onClick={() => trailer.current.close()}
          >
            <FiX />
          </button>
        </div>
        <video
          ref={video}
          aria-label="QUICR Trailer"
          controls
          playsInline
          preload="none"
          poster="/images/IMG_7064.jpg"
          src={aboutData.buttonModalVideoUrl}
          onError={() => setVideoFailed(true)}
        />
        {videoFailed ? (
          <p role="alert">
            {t("Der Trailer ist gerade nicht verfügbar.")}{" "}
            <Link
              to={{ search, hash: "#examples" }}
              onClick={() => trailer.current.close()}
            >
              {t("Weitere Beispiele ansehen")}
            </Link>
            .
          </p>
        ) : null}
      </dialog>
    </>
  );
}

export default Home;
